import React, { useState } from 'react';
import { BaseModal, Button, Input } from 'src/components';
import useModal from 'src/hooks/useModal';
import { useAlerts } from 'src/common/AlertManager';
import { isErrorResponseResult } from 'src/api/utils/errorHandlers';
import {
  useUpsertDestinationMutation,
  useTestDestinationMutation,
} from 'src/api/mutations/analyticsDestinationsMutations';
import {
  AnalyticsDestination,
  DestinationCredentials,
  DestinationType,
} from 'src/types/TAnalyticsDestinations';
import styles from './styles.module.scss';

const TYPE_LABEL: Record<DestinationType, string> = {
  ga4: 'Google Analytics 4',
  segment: 'Segment',
};

interface Props {
  destinationType: DestinationType;
  existing?: AnalyticsDestination;
}

function buildCredentials(
  destinationType: DestinationType,
  ga4MeasurementId: string,
  ga4ApiSecret: string,
  segmentWriteKey: string,
): DestinationCredentials {
  if (destinationType === 'ga4') {
    return { measurement_id: ga4MeasurementId.trim(), api_secret: ga4ApiSecret.trim() };
  }
  return { write_key: segmentWriteKey.trim() };
}

const AddDestinationModal: React.FC<Props> = ({ destinationType, existing }) => {
  const { closeModal } = useModal();
  const { showSuccess, showAlert } = useAlerts();

  const [measurementId, setMeasurementId] = useState(existing?.measurement_id ?? '');
  const [apiSecret, setApiSecret] = useState('');
  const [writeKey, setWriteKey] = useState('');
  const [testMsg, setTestMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const upsert = useUpsertDestinationMutation();
  const test = useTestDestinationMutation();

  const isGa4 = destinationType === 'ga4';
  const filled = isGa4
    ? measurementId.trim() !== '' && apiSecret.trim() !== ''
    : writeKey.trim() !== '';

  const credentials = () =>
    buildCredentials(destinationType, measurementId, apiSecret, writeKey);

  const handleTest = () => {
    setTestMsg(null);
    test.mutate(
      { destination_type: destinationType, credentials: credentials() },
      {
        onSuccess: (result) => {
          if (isErrorResponseResult(result)) {
            setTestMsg({ ok: false, text: 'Test request failed.' });
            return;
          }
          setTestMsg({ ok: result.ok, text: result.message });
        },
        onError: () => setTestMsg({ ok: false, text: 'Test request failed.' }),
      },
    );
  };

  const handleSave = () => {
    upsert.mutate(
      { destination_type: destinationType, credentials: credentials() },
      {
        onSuccess: (result) => {
          if (isErrorResponseResult(result)) {
            showAlert('Could not save destination.');
            return;
          }
          showSuccess(`${TYPE_LABEL[destinationType]} destination saved.`);
          closeModal();
        },
        onError: () => showAlert('Could not save destination.'),
      },
    );
  };

  return (
    <BaseModal>
      <div className={styles.modal}>
        <h2 className={styles.modalTitle}>
          {existing ? 'Edit' : 'Add'} {TYPE_LABEL[destinationType]} destination
        </h2>
        <p className={styles.modalDesc}>
          {isGa4
            ? 'CDN delivery events are sent to GA4 via the Measurement Protocol.'
            : 'CDN delivery events are sent to Segment via the HTTP Track API.'}
          {existing && ' Re-enter the secret to update it.'}
        </p>

        {isGa4 ? (
          <>
            <Input
              label="Measurement ID"
              placeholder="G-XXXXXXXXXX"
              value={measurementId}
              onChange={setMeasurementId}
            />
            <Input
              label="API secret"
              type="password"
              placeholder="••••••••"
              value={apiSecret}
              onChange={setApiSecret}
            />
          </>
        ) : (
          <Input
            label="Write key"
            type="password"
            placeholder="••••••••"
            value={writeKey}
            onChange={setWriteKey}
          />
        )}

        {testMsg && (
          <div className={testMsg.ok ? styles.testOk : styles.testFail}>{testMsg.text}</div>
        )}

        <div className={styles.modalActions}>
          <Button
            mode="secondary"
            outlined
            disabled={!filled}
            loading={test.isLoading}
            onClick={handleTest}
          >
            Test
          </Button>
          <div className={styles.modalActionsRight}>
            <Button mode="flat" onClick={() => closeModal()}>
              Cancel
            </Button>
            <Button
              mode="primary"
              disabled={!filled}
              loading={upsert.isLoading}
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

export default AddDestinationModal;
